import Box from 'UIKit/Box/Box'
import { BoxProps } from 'UIKit/Box/types'
import Image from 'UIKit/Image'
import Text from 'UIKit/Text'
import { useVariableLoadedListener } from 'hooks/useVariableLoadedListener'
import { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import colors from 'theme/ui/colors'
import { WheelGameRiskEnum } from 'views/InhouseGame/Wheel/config'
import { WheelBet, WheelSegments } from 'views/InhouseGame/Wheel/config/types'
import { flatPayout } from 'views/InhouseGame/Wheel/utils'

const WheelCircle: React.FC<
  {
    segments: WheelSegments
    risk: WheelGameRiskEnum
    bet: WheelBet
  } & BoxProps
> = ({ risk, segments, bet, ...props }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const canvasElement = useVariableLoadedListener(() => canvasRef.current)
  const [isRunning, setIsRunning] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)

  const payouts = useMemo(() => flatPayout(risk, segments), [risk, segments])

  const deg = useMemo(() => {
    return (currentIndex / segments) * 360
  }, [currentIndex, segments])

  useEffect(() => {
    if (!canvasElement) return

    const payouts = flatPayout(risk, segments)
    if (!payouts) return

    const ctx = canvasElement.getContext('2d')
    const circleRadius = 500
    const strokeWidthInner = 40
    const strokeWidthOuter = 52

    // Initial drawing (e.g., a circle)
    function drawCircle() {
      ctx.clearRect(0, 0, canvasElement.width, canvasElement.height) // Clear the canvas
      ctx.beginPath()
      const centerX = canvasElement.width / 2
      const centerY = canvasElement.height / 2
      const radius = circleRadius - strokeWidthOuter - (strokeWidthOuter - strokeWidthInner)

      for (let i = 0; i < payouts.length; i++) {
        const startAngle = ((i - 0.5) * 2 * Math.PI) / segments - Math.PI / 2
        const endAngle = ((i + 0.5) * 2 * Math.PI) / segments - Math.PI / 2

        ctx.beginPath()
        ctx.arc(centerX, centerY, radius, startAngle, endAngle)
        ctx.lineWidth = strokeWidthInner
        ctx.strokeStyle = payouts[i].color
        ctx.stroke()
      }

      for (let i = 0; i < segments; i++) {
        const startAngle = ((i - 0.5) * 2 * Math.PI) / segments - Math.PI / 2
        const endAngle = ((i - 0.5) * 2 * Math.PI) / segments - Math.PI / 2

        ctx.beginPath()

        ctx.arc(centerX, centerY, radius + strokeWidthInner / 2, startAngle, endAngle)
        ctx.lineTo(centerX, centerY)
        ctx.lineWidth = strokeWidthInner
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)'
        ctx.setLineDash([5, 8])
        ctx.lineWidth = 0.3
        ctx.stroke()
        ctx.setLineDash([])
      }

      ctx.beginPath()
      ctx.arc(centerX, centerY, radius / 2.5, 0, 2 * Math.PI)
      ctx.fillStyle = colors.backgroundAlt9
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)'
      ctx.lineWidth = 5
      ctx.stroke()
      ctx.fill()

      ctx.beginPath()
      ctx.arc(centerX, centerY, radius + strokeWidthInner - (strokeWidthOuter - strokeWidthInner), 0, 2 * Math.PI)
      ctx.lineWidth = strokeWidthOuter
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)'
      ctx.stroke()
    }

    // Call drawCircle initially
    drawCircle()
  }, [canvasElement, payouts, currentIndex])

  useEffect(() => {
    if (!bet) return
    const selectedPayoutsIndex = payouts
      .map((p, index) => ({ ...p, index }))
      .filter((p) => p.payout === bet.result)
      .map((p) => p.index)

    setIsRunning(true)
    const randomIndex = Math.floor(Math.random() * selectedPayoutsIndex.length - 1) + 1
    setCurrentIndex(selectedPayoutsIndex[randomIndex])

    const timeout = setTimeout(
      () => {
        setIsRunning(false)
      },
      bet.disabledDisplay ? 100 : 3950,
    )

    return () => {
      setIsRunning(false)
      clearTimeout(timeout)
    }
  }, [bet])

  useEffect(() => {
    setCurrentIndex(0)
  }, [payouts])

  return (
    <Box height="100%" pb={['100%', '', '', '', '0px']} width="100%" position="relative" {...props}>
      <Box
        position="absolute"
        width="18px"
        height="40px"
        top="-10px"
        left="50%"
        zIndex="3"
        style={{ transform: 'translateX(-50%)' }}
      >
        <Image src="/images/inhouse-game/wheel/pointer.png" width={50} height={101} />
      </Box>

      <Box position="absolute" top="50%" left="50%" zIndex="3" style={{ transform: 'translate(-50%, -50%)' }}>
        <Box style={{ transition: '0.2s', transform: `scale(${bet && !isRunning ? 1 : 0})` }} overflow="hidden">
          {bet && !isRunning && (
            <Text bold fontSize="30px" color={payouts[currentIndex]?.color}>
              {bet.result.toFixed(2)}x
            </Text>
          )}
        </Box>
      </Box>

      <StyledContainer
        $deg={isRunning ? -deg - 1080 : -deg}
        height="100%"
        position="absolute"
        width="100%"
        bottom="0"
        left="0"
        $disabledAnimation={bet?.disabledDisplay || !isRunning}
      >
        <canvas ref={canvasRef} width="1000" height="1000" />
      </StyledContainer>
    </Box>
  )
}

const StyledContainer = styled(Box)<{ $disabledAnimation?: boolean; $deg: number }>`
  display: flex;
  justify-content: center;
  transition: ${({ $disabledAnimation }) =>
    !$disabledAnimation ? ' cubic-bezier(0.15, 0.53, 0.59, 0.91) 3.95s' : 'none'};

  transform: rotate(${({ $deg }) => $deg}deg);
`
export default WheelCircle
